import { ReactElement, useState, useEffect } from 'react';
import { Button, Row, Col, Form, message, Modal, Alert } from 'antd';
import styles from './ButtonModalForm.module.less';
import AppForm from './ccx/common/AppForm';
import CcxComponentProps from '../core/CcxComponent';
import { Tooltip } from 'antd';
import { FormLayout } from 'antd/lib/form/Form';
import StretchedClick from './common/StretchedClick';
import usePiwikPro from '../core/hooks/usePiwikPro';
import {
    CANCEL_COMMON_MODAL_FORM,
    CLICK_EVENT,
    OPEN_COMMON_MODAL_FORM,
} from '../core/PiwikConstants';

interface Props extends CcxComponentProps {
    title: string | ReactElement;
    buttonText: string;
    buttonIcon?: ReactElement;
    onSubmit: Function;
    onSuccess: Function;
    onCancel?: Function;
    fields?: any[];
    children?: ReactElement;
    buttonType?: string;
    buttonClassName?: string;
    formValues?: any;
    formLayout?: FormLayout;
    overideValidationFunction?: Function;
    disabled?: boolean;
    disabledText?: string;
    formClassName?: string;
    submitText?: string;
    cancelText?: string;
    width?: number;
    leftCol?: number;
    rightCol?: number;
    useGrid?: boolean;
    onFormLoad?: Function;
    stretchedClick?: boolean;
    warningMessage?: ReactElement;
    handleModalForcely?: boolean;
    disabledSubmitBtn?: boolean;
}

function ButtonModalForm({
    title,
    buttonText,
    buttonIcon,
    className,
    onSubmit,
    onSuccess,
    onCancel,
    fields,
    children,
    buttonType,
    buttonClassName,
    formValues,
    formLayout,
    overideValidationFunction,
    testId,
    disabled,
    disabledText,
    formClassName,
    submitText = 'Submit',
    cancelText = 'Cancel',
    width = 720,
    leftCol = 5,
    rightCol = 4,
    useGrid,
    onFormLoad,
    stretchedClick = false,
    warningMessage,
    handleModalForcely = false,
    disabledSubmitBtn = false,
}: Props): ReactElement {
    const [form] = Form.useForm();
    const { trackPageView, trackEvent } = usePiwikPro();
    const [visible, setVisible] = useState(false);
    const [errorFields, setErrorFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState({});

    useEffect(() => {
        if (form && onFormLoad) {
            onFormLoad(form);
        }
    }, [form, onFormLoad]);

    useEffect(() => {
        if (!handleModalForcely) {
            setVisible(handleModalForcely);
            form.resetFields();
        }
    }, [handleModalForcely]);

    useEffect(() => {
        if (formValues) {
            setInitialFormValues(formValues);
        }
    }, [formValues]);

    const validateForm = async () => {
        if (overideValidationFunction) {
            return overideValidationFunction();
        }

        try {
            await form.validateFields();
            return true;
        } catch (e: any) {
            setErrorFields(e.errorFields);
            message.error(
                'One or more fields have errors. Please double check and try again.'
            );
            return false;
        }
    };

    const onSubmitButtonClick = async () => {
        const stepValid = await validateForm();

        if (stepValid) {
            setLoading(true);
            try {
                await onSubmit(form.getFieldsValue());
                onSuccess && onSuccess();
                onClose();
            } catch (e) {
                console.error('error e=', e);
            } finally {
                setLoading(false);
            }
        }
    };

    const onButtonClick = () => {
        trackPageView(`${OPEN_COMMON_MODAL_FORM}${title}`);
        setVisible(true);
    };

    const onClose = () => {
        form.resetFields();
        setErrorFields([]);
        setVisible(false);
        trackEvent(CLICK_EVENT, `${CANCEL_COMMON_MODAL_FORM}${title}`);
        onCancel && onCancel();
    };

    return (
        <>
            <Tooltip title={disabled ? disabledText : ''}>
                {stretchedClick ? (
                    <StretchedClick handleClick={onButtonClick} />
                ) : (
                    <Button
                        onClick={onButtonClick}
                        disabled={disabled}
                        icon={
                            buttonIcon && (
                                <span style={{ marginRight: 4 }}>
                                    {buttonIcon}
                                </span>
                            )
                        }
                        type={buttonType as any}
                        className={
                            className || buttonType === 'simple'
                                ? `${styles.ButtonModalFormTypeSimple} ${buttonClassName} ${styles.ButtonModalForm}`
                                : `${styles.ButtonModalForm} ${buttonClassName}`
                        }
                        data-testid={testId || 'ButtonModalForm'}
                    >
                        {buttonText}
                    </Button>
                )}
            </Tooltip>
            <Modal
                title={title}
                open={visible}
                data-testid={`${testId}Modal`}
                width={width}
                onCancel={onClose}
                footer={
                    <Row justify="end">
                        <Col>
                            <Button
                                data-testid={`${testId}CancelButton`}
                                onClick={onClose}
                            >
                                {cancelText}
                            </Button>
                            <Button
                                data-testid={`${testId}SubmitButton`}
                                loading={loading}
                                onClick={onSubmitButtonClick}
                                type="primary"
                                disabled={disabledSubmitBtn}
                            >
                                {submitText}
                            </Button>
                        </Col>
                    </Row>
                }
            >
                {children}
                <Form
                    layout={formLayout}
                    form={form}
                    scrollToFirstError={true}
                    initialValues={{ ...initialFormValues }}
                >
                    <AppForm
                        formClassName={formClassName}
                        fields={fields}
                        onPressEnter={onSubmitButtonClick}
                        errorFields={errorFields}
                        useGrid={useGrid}
                        overideValidationFunction={overideValidationFunction}
                    />
                </Form>
                {warningMessage && (
                    <Alert
                        type="warning"
                        showIcon={true}
                        message={'Warning'}
                        description={warningMessage}
                    />
                )}
            </Modal>
        </>
    );
}

export default ButtonModalForm;
